import {useTranslation, Trans, TFunction} from 'next-i18next'
import {
    Container,
    Content,
    ListItem,
    OrderedList,
    OrderedListItem,
    PageHeaderContainer,
    PageTitle,
    Paragraph,
    Section,
    SectionTitle,
    SubSection,
    Table, TableBody,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableWrapper,
    UnorderedList
} from "@/features/LegalPages/styles/textElements.styles";
import {useMemo} from "react";
import {COMPLAINTS_LINK, COMPONENTS, OUR_WEBSITE_LINK, SUPPORT_EMAIL} from "@/features/LegalPages/Constant/constants";

type TableComponentProps = {
    t: TFunction
    headerTranslationKey: string
    tableTranslationKeys: string[]
    className?: string
}

type TableDataTranslationProps = {
    t: TFunction
    translationKey: string
}

const TableDataTranslation = ({t, translationKey}: TableDataTranslationProps)=>(
    <Trans t={t} i18nKey={translationKey} components={{
        ...COMPONENTS,
        list: <UnorderedList className='list-in-table'/>,
        listItem: <ListItem className='default-list-item'/>
    }}/>
)

const TableComponent = ({t, headerTranslationKey, tableTranslationKeys, className = ''}: TableComponentProps) => {

    const tableDataRows = useMemo(()=>{
        return tableTranslationKeys.map((key, index) => (
                <TableRow key={index}>
                    <TableDataTranslation t={t} translationKey={key}/>
                </TableRow>
            ))
    },[])

    return(
        <TableWrapper>
            <Table className={className}>
                <TableHeader>
                    <TableRow>
                        <TableDataTranslation t={t} translationKey={headerTranslationKey}/>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {tableDataRows}
                </TableBody>
            </Table>
        </TableWrapper>
        )
}

const PrivacyPolicy = () => {
    const { t } = useTranslation(['privacyPolicy'])

    const categoriesOfPersonalData = ["privacyPolicy:tr1", "privacyPolicy:tr2", "privacyPolicy:tr3", "privacyPolicy:tr4", "privacyPolicy:tr5", "privacyPolicy:tr6", "privacyPolicy:tr7", "privacyPolicy:tr8", "privacyPolicy:tr9", "privacyPolicy:tr10", "privacyPolicy:tr11"]
    const purposesAndLegalBasis = ["privacyPolicy:tr12", "privacyPolicy:tr13", "privacyPolicy:tr14", "privacyPolicy:tr15", "privacyPolicy:tr16", "privacyPolicy:tr17", "privacyPolicy:tr18", "privacyPolicy:tr19", "privacyPolicy:tr20", "privacyPolicy:tr21", "privacyPolicy:tr22"]
    return (
        <Container>
            <Content>
                <PageHeaderContainer>
                    <PageTitle>{t('privacyPolicy:title')}</PageTitle>
                </PageHeaderContainer>

                <OrderedList>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:purposeAndScope" components={{...COMPONENTS}}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:processingOfPersonalData" components={{
                                ...COMPONENTS,
                                listItem: <ListItem className='processing-of-personal-data'/>
                            }}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:collectInformation">
                                <SectionTitle />
                            </Trans>
                            <OrderedList className='nested-list'>
                                <OrderedListItem>
                                    <SubSection>
                                        <Trans t={t} i18nKey="privacyPolicy:categoriesOfPersonalData" components={{
                                            ...COMPONENTS,
                                            paragraph: <Paragraph className='compact'/>,
                                        }}/>
                                        <TableComponent t={t} headerTranslationKey="privacyPolicy:categoriesOfPersonalDataHeader" tableTranslationKeys={categoriesOfPersonalData}/>
                                    </SubSection>
                                </OrderedListItem>
                                <OrderedListItem>
                                    <SubSection>
                                        <Trans t={t} i18nKey="privacyPolicy:purposesAndLegalBasis" components={{...COMPONENTS}}/>
                                        <TableComponent t={t} headerTranslationKey="privacyPolicy:purposesAndLegalBasisHeader" tableTranslationKeys={purposesAndLegalBasis} className='text-align'/>
                                        <Trans t={t} i18nKey="privacyPolicy:definitions" components={{
                                            paragraph: <Paragraph className='compact' />,
                                        }}/>
                                    </SubSection>
                                </OrderedListItem>
                            </OrderedList>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:collectPersonalData" components={{
                                ...COMPONENTS,
                                listItem: <ListItem className='list-type-lower-latin'/>
                            }}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:identification" components={{
                                ...COMPONENTS,
                                email: <a className="color-primary" href={`mailto:${SUPPORT_EMAIL}`}/>
                            }}
                            values={{emailForSupport: SUPPORT_EMAIL}}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:directMarketing" components={{...COMPONENTS}}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:automatedDecision" components={{
                                ...COMPONENTS,
                                linkItem: <a className="color-primary" href="#your-rights"/>
                            }}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:sharePersonalData" components={{
                                ...COMPONENTS,
                                listItem: <ListItem className='list-type-lower-latin'/>
                            }}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:internationalTransfer" components={{
                                ...COMPONENTS,
                                listItem: <ListItem className='list-type-lower-latin'/>
                            }}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:protectPersonalData" components={{...COMPONENTS}}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:howLongWeKeepPersonalData" components={{
                                ...COMPONENTS,
                                listItem: <ListItem className='list-type-lower-latin'/>
                            }}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section id='your-rights'>
                            <Trans t={t} i18nKey="privacyPolicy:yourRights" components={{
                                ...COMPONENTS,
                                listItem: <ListItem className='list-type-lower-latin'/>,
                                nestedListItem: <ListItem className='default-list-item'/>,
                                linkItem: <a className="color-primary"
                                             href={COMPLAINTS_LINK}/>,
                                email: <a className="color-primary" href={`mailto:${SUPPORT_EMAIL}`}/>
                                }}
                               values={{link: COMPLAINTS_LINK, emailForSupport: SUPPORT_EMAIL}}
                            />
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:cookiePolicy" components={{
                                ...COMPONENTS,
                                linkItem: <a className="color-primary" target='_blank' rel='noreferrer' href={OUR_WEBSITE_LINK}/>
                            }}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:linksToOtherWebsites" components={{...COMPONENTS}}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:changesOfThisPolicy" components={{...COMPONENTS}}/>
                        </Section>
                    </OrderedListItem>
                    <OrderedListItem>
                        <Section>
                            <Trans t={t} i18nKey="privacyPolicy:contactUs" components={{
                                ...COMPONENTS,
                                email: <a className="color-primary" href={`mailto:${SUPPORT_EMAIL}`}/>
                            }}
                            values={{emailForSupport: SUPPORT_EMAIL}}
                            />
                        </Section>
                    </OrderedListItem>
                </OrderedList>
            </Content>
        </Container>
    )
}

export default PrivacyPolicy
